import { createAsyncThunk } from '@reduxjs/toolkit';
import { externalIntegrationsApi } from './externalIntegrationsApi';

export const fetchExternalIntegrations = createAsyncThunk(
  'externalIntegrations/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().externalIntegrations;
      const params = {
        page: filters.page,
        page_size: filters.rowsPerPage,
      };
      if (filters.search) params.search = filters.search;

      const data = await externalIntegrationsApi.getExternalIntegrations(params);
      
      const results = Array.isArray(data) ? data : data.results;
      const count = Array.isArray(data) ? data.length : data.count;

      return {
        results: results || [],
        count: count || 0
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al obtener las integraciones externas'
      );
    }
  }
);

export const fetchSellersByAdminThunk = createAsyncThunk(
  'externalIntegrations/fetchSellers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await externalIntegrationsApi.getSellersList();
      const sellers = data.serialized_sellers?.map((seller) => ({
        id: seller.id,
        name: seller.company?.social_reason
      })) || [];
      return sellers;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'No se pudo obtener los sellers'
      );
    }
  }
);

/**
 * The same table, read as the internal team: every operator's services.
 *
 * Writes into the same slice as `fetchExternalIntegrations` — a session is
 * either staff or it is not, so the two never compete for the list.
 */
export const fetchStaffExternalIntegrations = createAsyncThunk(
  'externalIntegrations/fetchStaffAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().externalIntegrations;
      const data = await externalIntegrationsApi.getStaffExternalIntegrations({
        page: filters.page,
        page_size: filters.rowsPerPage,
        ...(filters.search ? { search: filters.search } : {}),
      });

      return { results: data.results || [], count: data.count || 0 };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Error al obtener las integraciones de la plataforma'
      );
    }
  }
);

export const createExternalIntegrationThunk = createAsyncThunk(
  'externalIntegrations/create',
  async ({ integrationData, adminId, isStaff } = {}, { dispatch, rejectWithValue }) => {
    try {
      await externalIntegrationsApi.createExternalIntegration(integrationData, adminId);
      // Awaited, so the action is still running until the list shows its result.
      await dispatch(isStaff ? fetchStaffExternalIntegrations() : fetchExternalIntegrations());
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear la integración externa'
      );
    }
  }
);

export const updateExternalIntegrationThunk = createAsyncThunk(
  'externalIntegrations/update',
  async ({ id, data, isStaff }, { dispatch, rejectWithValue }) => {
    try {
      await externalIntegrationsApi.updateExternalIntegration(id, data);
      await dispatch(isStaff ? fetchStaffExternalIntegrations() : fetchExternalIntegrations());
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar la integración externa'
      );
    }
  }
);

export const deleteExternalIntegrationThunk = createAsyncThunk(
  'externalIntegrations/delete',
  async ({ id, isStaff } = {}, { dispatch, rejectWithValue }) => {
    try {
      await externalIntegrationsApi.deleteExternalIntegration(id);
      // Until the list comes back without it, the row is still there to click.
      await dispatch(isStaff ? fetchStaffExternalIntegrations() : fetchExternalIntegrations());
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar la integración externa'
      );
    }
  }
);
